import type { WorkspacePersona } from "@/config/workspacePersona";

export type PersonaResolutionConfidence = "high" | "low";

export interface PersonaResolutionResult {
  persona: WorkspacePersona;
  confidence: PersonaResolutionConfidence;
  /** Matched tier used for confidence (not the exact substring). */
  matchedTier: WorkspacePersona | "default";
}

function normalize(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Longer phrases first so "assistant principal" wins over "principal" where needed. */
function sortPhrases(phrases: string[]): string[] {
  return [...phrases].sort((a, b) => b.length - a.length);
}

/**
 * Office / operational staff. Matched FIRST because phrases like "superintendent's secretary"
 * would otherwise resolve as `district_leader` from the word "superintendent".
 */
const CLASSIFIED_PHRASES = sortPhrases([
  "superintendent's administrative assistant",
  "superintendent's assistant",
  "superintendents assistant",
  "school secretary",
  "administrative assistant",
  "admin assistant",
  "office manager",
  "registrar",
  "attendance secretary",
  "attendance office",
  "front office",
  "front desk",
  "receptionist",
  "office assistant",
  "office staff",
  "secretary",
  "hr coordinator",
  "human resources",
  "payroll specialist",
  "payroll",
  "accounts payable",
  "bookkeeper",
  "communications coordinator",
  "communications director",
  "pr coordinator",
  "public relations",
  "transportation dispatcher",
  "dispatcher",
  "facilities coordinator",
  "facilities director",
  "maintenance coordinator",
  "federal programs coordinator",
  "curriculum and instruction coordinator",
  "curriculum & instruction coordinator",
  "curriculum coordinator",
  "central office staff",
  "operations coordinator",
]);

const DISTRICT_PHRASES = sortPhrases([
  "area superintendent",
  "assistant superintendent",
  "chief academic officer",
  "central office",
  "district admin",
  "district administrator",
  "district leadership",
  "district level",
  "district office",
  "district leader",
  "multiple schools",
  "network lead",
  "oversee schools",
  "superintendent",
  "cabinet",
  "cao",
  "district wide",
  "district-wide",
  "at the district",
  "for the district",
]);

const LEADER_PHRASES = sortPhrases([
  "assistant principal",
  "assistant principalship",
  "building leader",
  "curriculum lead",
  "dean of instruction",
  "dean of students",
  "department chair",
  "head of school",
  "instructional coach",
  "instructional lead",
  "lead teachers",
  "principal",
  "principalship",
  "school leader",
  "school leadership",
  "school-wide",
  "vice principal",
  "vp ",
  " ap ",
  " ap,",
  "associate principal",
]);

const SUPPORT_PHRASES = sortPhrases([
  // Instructional support
  "instructional aide",
  "instructional assistant",
  "instructional support",
  "teaching assistant",
  "special education paraprofessional",
  "sped paraprofessional",
  "sped para",
  "sped aide",
  "title i aide",
  "title 1 aide",
  "title one aide",
  "ell aide",
  "ell paraprofessional",
  "ell paraeducator",
  "esl aide",
  "esl assistant",
  "esl paraprofessional",
  "1:1 aide",
  "one to one aide",
  "one-on-one aide",
  "1 on 1 aide",
  // Behavioral / therapeutic
  "behavioral intervention aide",
  "behavior intervention aide",
  "behavior specialist",
  "crisis intervention aide",
  "intervention specialist",
  "interventionist",
  // Related services
  "occupational therapy aide",
  "ot aide",
  "occupational therapist",
  "speech-language pathology assistant",
  "speech language pathology assistant",
  "speech pathology assistant",
  "speech therapist",
  "speech pathologist",
  "speech language",
  "speech-language",
  "slpa",
  "slp ",
  "physical therapy aide",
  "pt aide",
  "physical therapist",
  // Operational student support
  "library aide",
  "library media aide",
  "media center aide",
  "media aide",
  "library assistant",
  "school librarian",
  "librarian",
  "lunch monitor",
  "recess monitor",
  "playground monitor",
  "lunch lady",
  "lunch staff",
  "cafeteria staff",
  "cafeteria worker",
  "cafeteria aide",
  "cafeteria",
  "health aide",
  "school nurse aide",
  "attendance aide",
  "front office aide",
  // Specialized settings
  "life skills aide",
  "self-contained classroom aide",
  "self contained aide",
  "autism support aide",
  "transition program aide",
  "transition aide",
  // School psych / counselor / nurse (often non-classroom)
  "school counselor",
  "counselor",
  "school psychologist",
  "school nurse",
  // Generic
  "paraprofessional",
  "paraeducator",
  "parapro",
  "para",
  "aide",
  "aid",
  "substitute",
  "sub teacher",
  "tutor",
]);

const TEACHER_PHRASES = sortPhrases([
  "classroom teacher",
  "gen ed",
  "general education",
  "homeroom",
  "kindergarten teacher",
  "math teacher",
  "music teacher",
  "of record",
  "pe teacher",
  "physical education teacher",
  "science teacher",
  "special education teacher",
  "sped teacher",
  "ela teacher",
  "english teacher",
  "teacher",
  "teaching",
  "educator",
  "instructor",
]);

/**
 * Two-pass match:
 * 1. Substring (input contains a phrase) — handles full words & natural phrases.
 * 2. Prefix (a phrase starts with the input) — handles partial typing like
 *    "lunch" → "lunch lady", "reception" → "receptionist", "secret" → "secretary".
 *    Requires input ≥ 4 chars to avoid noisy 1–3 char prefixes.
 */
function firstMatchingPhrase(normalized: string, phrases: string[]): string | null {
  for (const p of phrases) {
    if (normalized.includes(p)) return p;
  }
  if (normalized.length >= 4) {
    for (const p of phrases) {
      if (p.startsWith(normalized)) return p;
    }
  }
  return null;
}

/**
 * Priority: classified_staff → district_leader → leader → support_staff → teacher → default teacher.
 * Classified runs first so "superintendent's secretary" resolves correctly.
 * Confidence is `high` when a tier matched; `low` only when fallback is used on longer input.
 */
export function resolvePersonaFromHelperText(raw: string): PersonaResolutionResult {
  const normalized = normalize(raw);
  if (!normalized) {
    return { persona: "teacher", confidence: "low", matchedTier: "default" };
  }

  if (firstMatchingPhrase(normalized, CLASSIFIED_PHRASES)) {
    return { persona: "classified_staff", confidence: "high", matchedTier: "classified_staff" };
  }
  if (firstMatchingPhrase(normalized, DISTRICT_PHRASES)) {
    return { persona: "district_leader", confidence: "high", matchedTier: "district_leader" };
  }
  if (firstMatchingPhrase(normalized, LEADER_PHRASES)) {
    return { persona: "leader", confidence: "high", matchedTier: "leader" };
  }
  if (firstMatchingPhrase(normalized, SUPPORT_PHRASES)) {
    return { persona: "support_staff", confidence: "high", matchedTier: "support_staff" };
  }
  if (firstMatchingPhrase(normalized, TEACHER_PHRASES)) {
    return { persona: "teacher", confidence: "high", matchedTier: "teacher" };
  }

  const lowConfidenceFallback = normalized.length >= 12;
  return {
    persona: "teacher",
    confidence: lowConfidenceFallback ? "low" : "high",
    matchedTier: "default",
  };
}
